// Recommended new version for: apps/admin/src/config/firebase.ts

import { initializeApp } from "firebase/app";
import { getAuth, connectAuthEmulator, setPersistence, browserSessionPersistence, signOut } from "firebase/auth";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";


// --- Admin Configuration ----------------------------------------------------
// This file is part of the admin bundle, so it ONLY uses admin environment variables.
// There is no need for client config or host-based switching.
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_ADMIN_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID, // Ensure this is the ADMIN App ID
};

// --- Hard Guards: Fail early if config is missing ---------------------------
if (!firebaseConfig.apiKey || !firebaseConfig.projectId || !firebaseConfig.appId) {
  throw new Error(
    "CRITICAL: Missing required Firebase admin config. Check your .env file for REACT_APP_FIREBASE_ADMIN_API_KEY, PROJECT_ID, and APP_ID."
  );
}

const bucket = firebaseConfig.storageBucket;
if (!bucket || !/\.firebasestorage\.app$/i.test(bucket)) {
  throw new Error(
    `Invalid or missing storage bucket "${bucket}". Use "<project-id>.firebasestorage.app".`
  );
}

// --- Environment & Region ---------------------------------------------------
const ENV =
  firebaseConfig.projectId === "valutten-dev" ? "dev" :
  firebaseConfig.projectId === "valutten-stage"   ? "stage" :
                                                     "prod";
const FUNCTIONS_REGION = "australia-southeast1"; // Always use AU region for all environments

  // console.log(`[Admin Environment] project=${firebaseConfig.projectId}, env=${ENV}, region=${FUNCTIONS_REGION}`);

// --- Initialize Firebase ----------------------------------------------------
const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);

// Enterprise security: session clears on browser close.
setPersistence(auth, browserSessionPersistence).catch(() => {});

// One-time migration: clear legacy localStorage auth tokens left by browserLocalPersistence.
const SESSION_MIGRATION_KEY = 'admin-session-v1';
if (typeof window !== 'undefined' && !localStorage.getItem(SESSION_MIGRATION_KEY)) {
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith('firebase:authUser:')) {
      localStorage.removeItem(key);
    }
  });
  localStorage.setItem(SESSION_MIGRATION_KEY, 'true');
  signOut(auth).catch(() => {});
}

export const db = getFirestore(app);
export const functions = getFunctions(app, FUNCTIONS_REGION);
export const storage = getStorage(app);

// --- AppCheck Configuration (Jeff Dean Enterprise Approach) ----------------
// Production: Strict reCAPTCHA Enterprise enforcement
// Staging/Dev: Debug mode enabled for testing (prevents throttling)
// Debug tokens MUST be registered in Firebase Console -> App Check -> Debug Tokens
// Static token used for team consistency
if (typeof window !== 'undefined') {
  if (ENV !== 'prod') {
    // Debug token loaded from environment variable (set in .env.local)
    // Fallback `true` enables Firebase auto-generated debug token
    (window as any).FIREBASE_APPCHECK_DEBUG_TOKEN = process.env.REACT_APP_APPCHECK_DEBUG_TOKEN || true;
    console.log('[AppCheck] Debug mode enabled for', ENV, 'environment');
  }

  const siteKey = process.env.REACT_APP_RECAPTCHA_ENTERPRISE_SITE_KEY;
  if (siteKey) {
    try {
      initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(siteKey),
        isTokenAutoRefreshEnabled: true,
      });
      console.log('[AppCheck] Initialized successfully');
    } catch (e: any) {
      console.error('[AppCheck] Initialization failed:', e);
    }
  } else {
    console.warn('[AppCheck] Not initialized: missing REACT_APP_RECAPTCHA_ENTERPRISE_SITE_KEY');
  }
}

// --- Emulators (opt-in) -------------------------------------------------------
const host = window.location.hostname;
const USE_EMULATORS = process.env.REACT_APP_USE_EMULATORS === "true";

if (host === "localhost" && USE_EMULATORS) {
  // console.log("DEV: Connecting to Firebase Emulators…");
  connectAuthEmulator(auth, "http://localhost:9099");
  connectFirestoreEmulator(db, "localhost", 8081);
  connectFunctionsEmulator(functions, "localhost", 5001);
  connectStorageEmulator(storage, "localhost", 9199);
}

export default app;